import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { getCookie } from '../contexts/AuthContext';
import { Calendar, CheckCircle2, Copy, Check, Image as ImageIcon, Loader2, X, Film, Play, Pencil, MessageSquare, Send, Upload, ShieldCheck, ChevronDown } from 'lucide-react';

/*
  Sezione "Da approvare" della pagina del cliente (28/10/2026).
  I contenuti arrivano da pubblica (/api/approvazioni/:clientId/…, passato da
  nginx): per ogni reel il video della versione giusta, la copertina e il copy.
  Il cliente guarda e preme "Approva": la conferma la registra pubblica,
  come quando la segniamo noi a mano. Dal 28/10/2026 notte (il titolare) può
  anche "Chiedere una modifica": sceglie cosa (video, copertina o foto; la caption la corregge direttamente)
  e la scrive; pubblica la registra come conferma "modifiche" con la sua nota.
*/

export interface ContenutoApprovazione {
  id: string;
  titolo: string;
  tipo: 'reel' | 'post';
  data_roma: string | null;
  ora_roma: string | null;
  caption: string | null;
  copertina_url: string | null;
  video_url: string | null;
  /** Le foto del post, in ordine (29/10/2026). */
  slide_url?: string[];
  /** Quali slide sono video (03/11/2026, caroselli con foto e clip). */
  slide_video?: boolean[];
  approvato: boolean;
  approvato_il: string | null;
  si_puo_approvare: boolean;
  modifiche_richieste?: boolean;
  modifica_richiesta?: RichiestaModifica | null;
  /** Quando e su quali reti esce (02/11/2026): già su Metricool, uscito o previsto dal calendario. */
  uscita?: { data_roma: string; ora_roma: string; reti: string[]; stato: 'su_metricool' | 'uscito' | 'previsto' } | null;
}

export interface RichiestaModifica {
  il: string;
  cosa: string[];
  testo: string;
}

// Cosa si può chiedere di cambiare (29/10/2026, il titolare: «testo è da togliere che il testo viene
// approvato come prima cosa e altro toglilo»). La caption non si chiede: si corregge direttamente.
const COSE_MODIFICA_REEL: { id: string; nome: string }[] = [
  { id: 'video', nome: 'Video' },
  { id: 'copertina', nome: 'Copertina' },
];
const COSE_MODIFICA_POST: { id: string; nome: string }[] = [{ id: 'foto', nome: 'Foto' }];
// Le richieste già mandate possono avere anche le voci di prima.
const NOMI_MODIFICA: Record<string, string> = { video: 'Video', copertina: 'Copertina', foto: 'Foto', testo: 'Testo', altro: 'Altro' };
const TESTO_MODIFICA_MAX = 1000;

const quandoLeggibile = (iso: string) => {
  try {
    return format(parseISO(iso), "d MMMM 'alle' HH:mm", { locale: it });
  } catch {
    return iso;
  }
};

const INK = '#13161C';
const MUTED = '#8B92A0';
const LINE = '#EBEDF1';

const dataLeggibile = (d: string | null, ora: string | null) => {
  if (!d) return 'Data da definire';
  try {
    const s = format(parseISO(d), 'EEEE d MMMM', { locale: it });
    return `${s.charAt(0).toUpperCase()}${s.slice(1)}${ora ? ` · ore ${ora}` : ''}`;
  } catch {
    return d;
  }
};

/**
 * Il testo del post, sempre tutto in vista e modificabile dal cliente: si salva
 * in social con la stessa chiamata con cui il cliente corregge i testi nel calendario.
 */
const Caption: React.FC<{
  clientId: string;
  contentId: string;
  testo: string | null;
  onSalvato?: (t: string) => void;
  /** Cresce quando il cliente sceglie «Caption» fra le modifiche: il testo si apre pronto da scrivere. */
  apri?: number;
}> = ({ clientId, contentId, testo, onSalvato, apri = 0 }) => {
  const [salvato, setSalvato] = useState(testo ?? '');
  const [bozza, setBozza] = useState(testo ?? '');
  const [copiato, setCopiato] = useState(false);
  const [stato, setStato] = useState<'fermo' | 'salvo' | 'salvato' | 'errore'>('fermo');
  const area = useRef<HTMLTextAreaElement>(null);
  const cambiato = bozza.trim() !== salvato.trim();

  // La caption scritta dal Copywriter arriva dopo (reel appena caricato): se il campo era
  // ancora vuoto e nessuno ci ha scritto, si riempie da solo.
  useEffect(() => {
    const t = (testo ?? '').trim();
    if (!t || salvato.trim() || bozza.trim()) return;
    setSalvato(t);
    setBozza(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testo]);

  // L'area cresce col testo: niente scroll interno, si legge tutto.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    const misura = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    misura();
    // I font arrivano dopo e la larghezza cambia col telefono girato: si rimisura, se no l'ultima riga resta tagliata.
    document.fonts?.ready.then(misura).catch(() => undefined);
    window.addEventListener('resize', misura);
    return () => window.removeEventListener('resize', misura);
  }, [bozza]);

  useEffect(() => {
    const el = area.current;
    if (!apri || !el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
  }, [apri]);

  const copia = async () => {
    try {
      await navigator.clipboard.writeText(bozza);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 1800);
    } catch {
      /* il browser non lo permette: il testo resta selezionabile */
    }
  };
  const salva = async () => {
    setStato('salvo');
    try {
      const r = await fetch(`/api/clients/${clientId}/contents/${contentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_copy: bozza.trim() }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setSalvato(bozza.trim());
      setBozza(bozza.trim());
      onSalvato?.(bozza.trim());
      setStato('salvato');
      setTimeout(() => setStato('fermo'), 2200);
    } catch {
      setStato('errore');
    }
  };

  return (
    <div className="rounded-xl" style={{ backgroundColor: '#F4F5F7' }}>
      <div className="flex items-center justify-between px-4 pt-3">
        <span className="text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: MUTED }}>Testo del post</span>
        {bozza.trim() && (
          <button onClick={copia} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md hover:bg-white transition" style={{ color: INK }}>
            {copiato ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copiato ? 'Copiato' : 'Copia'}
          </button>
        )}
      </div>
      <textarea
        ref={area}
        value={bozza}
        onChange={(e) => {
          setBozza(e.target.value);
          if (stato !== 'salvo') setStato('fermo');
        }}
        rows={3}
        placeholder="Il testo del post è in preparazione. Se vuoi puoi scriverlo tu qui."
        className="block w-full resize-none overflow-hidden bg-transparent border-0 px-4 pb-3 pt-2 text-[15px] leading-relaxed focus:outline-none focus:ring-0 rounded-b-xl"
        style={{ color: INK }}
        aria-label="Testo del post, modificabile"
      />
      <div className="flex flex-wrap items-center gap-2 px-4 pb-3 min-h-[20px]">
        {cambiato ? (
          <>
            <button
              onClick={salva}
              disabled={stato === 'salvo'}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: INK }}
            >
              {stato === 'salvo' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Salva il testo
            </button>
            <button onClick={() => { setBozza(salvato); setStato('fermo'); }} className="px-3 py-2 rounded-lg text-sm font-medium" style={{ color: INK }}>
              Annulla
            </button>
          </>
        ) : stato === 'salvato' ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: '#1F7A4D' }}><Check className="w-3.5 h-3.5" /> Testo salvato</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs" style={{ color: MUTED }}><Pencil className="w-3 h-3" /> Puoi modificare il testo direttamente qui</span>
        )}
        {stato === 'errore' && <span className="text-xs" style={{ color: '#B42318' }}>Non siamo riusciti a salvare, riprova fra poco.</span>}
      </div>
    </div>
  );
};

/** Copertina in piccolo, si apre a tutto schermo. */
const Copertina: React.FC<{ url: string | null; onApri: (u: string) => void; grande?: boolean }> = ({ url, onApri, grande }) => {
  const cls = grande ? 'w-full aspect-[9/16]' : 'w-[72px] aspect-[9/16]';
  if (!url) {
    return (
      <div className={`${cls} rounded-lg flex flex-col items-center justify-center gap-1 text-center`} style={{ backgroundColor: '#F4F5F7', border: `1px dashed ${LINE}`, color: MUTED }}>
        <ImageIcon className="w-4 h-4" />
        <span className="text-[10px] leading-tight px-1">Copertina in arrivo</span>
      </div>
    );
  }
  return (
    <button onClick={() => onApri(url)} className={`${cls} rounded-lg overflow-hidden flex-shrink-0 transition hover:opacity-90`} style={{ border: `1px solid ${LINE}` }} title="Apri la copertina">
      <img src={url} alt="Copertina" className="w-full h-full object-cover" loading="lazy" draggable={false} />
    </button>
  );
};

/**
 * Le foto del post come su Instagram (29/10/2026, il titolare: «la parte del post non si vede
 * bene sul link»): 4:5, si sfogliano con le frecce o col dito, pallini sotto.
 * 30/10/2026 («lo slider delle img del carosello non funziona bene»): le foto stanno tutte in
 * fila e scorrono seguendo il dito o il mouse, così non si ricaricano a ogni cambio; uno
 * swipe non apre più lo zoom per sbaglio; pallini cliccabili e frecce della tastiera.
 */
const Foto: React.FC<{ urls: string[]; video?: boolean[]; onApri?: (u: string) => void }> = ({ urls, video, onApri }) => {
  const [i, setI] = useState(0);
  const [dx, setDx] = useState(0);
  const [trascina, setTrascina] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const inizio = useRef<{ x: number; y: number; id: number; asse: 'x' | 'y' | null } | null>(null);
  const mosso = useRef(false);
  const n = urls.length;
  // Se cambiano le foto (slide ricaricate) si riparte dalla prima.
  const chiave = urls.join('|');
  useEffect(() => setI(0), [chiave]);
  if (!n) {
    return (
      <div className="w-full aspect-[4/5] rounded-2xl flex flex-col items-center justify-center gap-2" style={{ backgroundColor: '#F4F5F7', color: MUTED }}>
        <ImageIcon className="w-6 h-6" />
        <span className="text-sm">Foto in preparazione</span>
      </div>
    );
  }
  const vai = (k: number) => setI(Math.min(n - 1, Math.max(0, k)));
  const giu = (e: React.PointerEvent) => {
    if (n < 2 || (e.pointerType === 'mouse' && e.button !== 0)) return;
    inizio.current = { x: e.clientX, y: e.clientY, id: e.pointerId, asse: null };
    mosso.current = false;
  };
  const muovi = (e: React.PointerEvent) => {
    const s = inizio.current;
    if (!s || s.id !== e.pointerId) return;
    const mx = e.clientX - s.x;
    const my = e.clientY - s.y;
    if (!s.asse) {
      if (Math.abs(mx) < 6 && Math.abs(my) < 6) return;
      // Movimento verticale: lascia scorrere la pagina.
      s.asse = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
      if (s.asse === 'y') { inizio.current = null; return; }
      box.current?.setPointerCapture(e.pointerId);
      setTrascina(true);
    }
    mosso.current = true;
    // Ai bordi resiste, come Instagram.
    const bordo = (i === 0 && mx > 0) || (i === n - 1 && mx < 0);
    setDx(bordo ? mx / 3 : mx);
  };
  const su = (e: React.PointerEvent) => {
    const s = inizio.current;
    inizio.current = null;
    if (!s || s.id !== e.pointerId || s.asse !== 'x') return;
    const largo = box.current?.offsetWidth || 1;
    const mx = e.clientX - s.x;
    if (Math.abs(mx) > largo * 0.18) vai(i + (mx < 0 ? 1 : -1));
    setDx(0);
    setTrascina(false);
  };
  const tasto = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); vai(i + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); vai(i - 1); }
  };
  const sposta = `calc(${-i * 100}% + ${dx}px)`;
  return (
    <div>
      <div
        ref={box}
        tabIndex={0}
        onKeyDown={tasto}
        onPointerDown={giu}
        onPointerMove={muovi}
        onPointerUp={su}
        onPointerCancel={su}
        className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden bg-[#F4F5F7] select-none outline-none"
        style={{ touchAction: 'pan-y' }}
      >
        <div
          className="absolute inset-0 flex"
          style={{ transform: `translate3d(${sposta},0,0)`, transition: trascina ? 'none' : 'transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)' }}
        >
          {urls.map((u, k) => video?.[k] ? (
            // Una clip del carosello (03/11/2026): si guarda col player, niente zoom.
            <video
              key={u + k}
              src={u}
              controls
              playsInline
              preload="metadata"
              className="w-full h-full flex-shrink-0 object-cover bg-black"
            />
          ) : (
            <img
              key={u + k}
              src={u}
              alt={`Foto ${k + 1}`}
              className="w-full h-full flex-shrink-0 object-cover cursor-zoom-in"
              draggable={false}
              // Tutte subito (al massimo 10): in lazy Chrome non carica quelle nascoste di lato.
              decoding="async"
              onClick={() => {
                if (mosso.current) { mosso.current = false; return; }
                onApri?.(u);
              }}
            />
          ))}
        </div>
        {n > 1 && (
          <>
            <span className="absolute top-3 right-3 text-xs font-semibold text-white px-2.5 py-1 rounded-full pointer-events-none" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>{i + 1}/{n}</span>
            {i > 0 && (
              <button onPointerDown={(e) => e.stopPropagation()} onClick={() => vai(i - 1)} aria-label="Foto precedente" className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center text-lg font-bold" style={{ color: INK }}>‹</button>
            )}
            {i < n - 1 && (
              <button onPointerDown={(e) => e.stopPropagation()} onClick={() => vai(i + 1)} aria-label="Foto successiva" className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center text-lg font-bold" style={{ color: INK }}>›</button>
            )}
          </>
        )}
      </div>
      {n > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {urls.map((_, k) => (
            <button key={k} onClick={() => vai(k)} aria-label={`Foto ${k + 1}`} className="p-1">
              <span className="block w-1.5 h-1.5 rounded-full transition-colors" style={{ backgroundColor: k === i ? '#0095F6' : '#C7C7C7' }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Il reel come lo vedrà nel profilo: ferma, si vede tutta la copertina col play
 * piccolo in basso; in pausa o alla fine torna la copertina.
 */
const Video: React.FC<{ url: string | null; poster: string | null }> = ({ url, poster }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [inPlay, setInPlay] = useState(false);
  if (!url) {
    return (
      <div className="w-full aspect-[9/16] rounded-2xl flex flex-col items-center justify-center gap-2" style={{ backgroundColor: '#F4F5F7', color: MUTED }}>
        <Film className="w-6 h-6" />
        <span className="text-sm">Video in montaggio</span>
      </div>
    );
  }
  return (
    <div className="relative w-full aspect-[9/16] rounded-2xl overflow-hidden bg-black">
      <video
        ref={ref}
        // #t=0.1: senza copertina il browser mostra il primo fotogramma invece del nero.
        src={poster ? url : `${url}#t=0.1`}
        controls={inPlay}
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-contain"
        onPlay={() => setInPlay(true)}
        onPause={() => setInPlay(false)}
        onEnded={() => setInPlay(false)}
      />
      {!inPlay && (
        <button
          onClick={() => void ref.current?.play()}
          className="absolute inset-0 w-full h-full text-left"
          aria-label="Guarda il video"
        >
          {poster && <img src={poster} alt="Copertina del reel" className="absolute inset-0 w-full h-full object-cover" draggable={false} />}
          <span
            className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 pl-3 pr-4 py-2 rounded-full text-[13px] font-semibold shadow-lg"
            style={{ backgroundColor: 'rgba(255,255,255,0.94)', color: INK }}
          >
            <Play className="w-4 h-4 fill-current" />
            Guarda il video
          </span>
        </button>
      )}
    </div>
  );
};

/** La richiesta già mandata: il cliente vede cosa ha chiesto e quando. */
const RichiestaInviata: React.FC<{ r: RichiestaModifica; appena: boolean }> = ({ r, appena }) => {
  const cosa = r.cosa.map((k) => NOMI_MODIFICA[k] ?? k);
  return (
    <div className="rounded-xl px-4 py-3" style={{ backgroundColor: '#FFF7E8', border: '1px solid #F3DDB0' }} role="status">
      <p className="flex items-start gap-1.5 text-[13px] font-semibold leading-snug" style={{ color: '#8A5A00' }}>
        <MessageSquare className="w-4 h-4 mt-px flex-shrink-0" />
        <span>
          {appena ? 'Richiesta inviata' : 'Hai chiesto una modifica'} il {quandoLeggibile(r.il)}
          {cosa.length > 0 && <span className="font-medium"> · {cosa.join(', ')}</span>}
        </span>
      </p>
      <p className="mt-1.5 text-[15px] leading-relaxed whitespace-pre-line break-words" style={{ color: INK }}>{r.testo}</p>
      <p className="mt-2 text-[12px]" style={{ color: '#8A5A00' }}>Ti prepariamo la nuova versione. Se intanto ti va bene così, puoi comunque approvare.</p>
    </div>
  );
};

/** Il piccolo form «Chiedi una modifica»: cosa e come. */
const FormModifica: React.FC<{
  post: boolean;
  onInvia: (cosa: string[], testo: string) => Promise<void>;
  onAnnulla: () => void;
  onCaption: () => void;
}> = ({ post, onInvia, onAnnulla, onCaption }) => {
  const voci = post ? COSE_MODIFICA_POST : COSE_MODIFICA_REEL;
  const [cosa, setCosa] = useState<string[]>([]);
  const [testo, setTesto] = useState('');
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const pronto = cosa.length > 0 && testo.trim().length >= 3 && !invio;

  useEffect(() => {
    area.current?.focus({ preventScroll: true });
  }, []);
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 96)}px`;
  }, [testo]);

  const invia = async () => {
    if (!pronto) return;
    setInvio(true);
    setErrore(null);
    try {
      await onInvia(cosa, testo.trim());
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Non è andata, riprova');
      setInvio(false);
    }
  };

  return (
    <div className="rounded-xl p-4 flex flex-col gap-3" style={{ backgroundColor: '#F4F5F7', border: `1px solid ${LINE}` }}>
      <div>
        <p className="text-[15px] font-semibold" style={{ color: INK }}>Cosa vuoi cambiare?</p>
        <p className="text-[13px] mt-0.5" style={{ color: MUTED }}>Puoi sceglierne più di uno.</p>
      </div>
      <div className={`grid gap-2 ${post ? 'grid-cols-2' : 'grid-cols-3'}`} role="group" aria-label="Cosa vuoi cambiare">
        {voci.map((k) => {
          const on = cosa.includes(k.id);
          return (
            <button
              key={k.id}
              type="button"
              aria-pressed={on}
              onClick={() => setCosa((p) => (on ? p.filter((x) => x !== k.id) : [...p, k.id]))}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-[14px] font-medium transition"
              style={on ? { backgroundColor: INK, color: '#fff', border: `1px solid ${INK}` } : { backgroundColor: '#fff', color: INK, border: `1px solid ${LINE}` }}
            >
              {on && <Check className="w-4 h-4" />}
              {k.nome}
            </button>
          );
        })}
        {/* La caption non passa da una richiesta: apre il testo del post e lo si cambia lì. */}
        <button
          type="button"
          onClick={onCaption}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-[14px] font-medium transition"
          style={{ backgroundColor: '#fff', color: INK, border: `1px solid ${LINE}` }}
        >
          <Pencil className="w-4 h-4" />
          Caption
        </button>
      </div>
      <div className="rounded-lg bg-white" style={{ border: `1px solid ${LINE}` }}>
        <textarea
          ref={area}
          value={testo}
          onChange={(e) => setTesto(e.target.value.slice(0, TESTO_MODIFICA_MAX))}
          maxLength={TESTO_MODIFICA_MAX}
          rows={3}
          placeholder="Scrivi qui cosa cambiare, ad esempio: «nella copertina metterei la foto del negozio»"
          className="block w-full resize-none overflow-hidden bg-transparent border-0 px-3 pt-3 pb-1 text-[15px] leading-relaxed focus:outline-none focus:ring-0 rounded-lg"
          style={{ color: INK }}
          aria-label="Cosa vuoi cambiare"
        />
        <p className="px-3 pb-2 text-right text-[11px]" style={{ color: MUTED }}>{testo.length}/{TESTO_MODIFICA_MAX}</p>
      </div>
      {errore && <p className="text-sm" style={{ color: '#B42318' }}>{errore}</p>}
      <div className="flex gap-2">
        <button
          onClick={onAnnulla}
          disabled={invio}
          className="flex-none whitespace-nowrap inline-flex items-center justify-center px-4 py-3 rounded-xl text-[14px] sm:text-[15px] font-medium transition bg-white"
          style={{ color: INK, border: `1px solid ${LINE}` }}
        >
          Annulla
        </button>
        <button
          onClick={invia}
          disabled={!pronto}
          className="flex-auto whitespace-nowrap inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[14px] sm:text-[15px] font-semibold text-white transition disabled:opacity-40"
          style={{ backgroundColor: INK }}
        >
          {invio ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
          Invia la richiesta
        </button>
      </div>
    </div>
  );
};

const SchedaDaApprovare: React.FC<{
  clientId: string;
  c: ContenutoApprovazione;
  onApprova: (id: string) => Promise<void>;
  onChiediModifica: (id: string, cosa: string[], testo: string) => Promise<RichiestaModifica>;
  onApriCopertina: (u: string) => void;
}> = ({ clientId, c, onApprova, onChiediModifica, onApriCopertina }) => {
  const post = c.tipo === 'post';
  const [modo, setModo] = useState<'scelta' | 'approva' | 'modifica'>('scelta');
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [richiesta, setRichiesta] = useState<RichiestaModifica | null>(c.modifica_richiesta ?? null);
  const [appena, setAppena] = useState(false);
  const [apriCaption, setApriCaption] = useState(0);
  const approva = async () => {
    setInvio(true);
    setErrore(null);
    try {
      await onApprova(c.id);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Non è andata, riprova');
      setInvio(false);
      setModo('scelta');
    }
  };
  const chiedi = async (cosa: string[], testo: string) => {
    const r = await onChiediModifica(c.id, cosa, testo);
    setRichiesta(r);
    setAppena(true);
    setModo('scelta');
  };
  return (
    <article className="rounded-2xl p-4 sm:p-6 bg-white" style={{ border: `1px solid ${LINE}` }}>
      <div className="grid gap-5 md:gap-8 md:grid-cols-[minmax(0,340px)_1fr]">
        <div className="w-full max-w-[340px] mx-auto md:mx-0">
          {post ? <Foto urls={c.slide_url ?? []} video={c.slide_video} onApri={onApriCopertina} /> : <Video url={c.video_url} poster={c.copertina_url} />}
        </div>
        <div className="flex flex-col gap-4 min-w-0">
          <div>
            {/* Quando esce: deve leggersi subito (28/10/2026, l'utente: «voglio proprio che si legga»). */}
            <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ backgroundColor: '#F4F5F7', border: `1px solid ${LINE}` }}>
              <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-white" style={{ border: `1px solid ${LINE}` }}>
                <Calendar className="w-5 h-5" style={{ color: INK }} />
              </span>
              <div className="min-w-0">
                <p className="text-[12px] uppercase tracking-[0.14em] font-medium" style={{ color: MUTED }}>Contenuto programmato per il</p>
                <p className="text-[17px] sm:text-lg font-semibold leading-snug" style={{ color: INK }}>{dataLeggibile(c.data_roma, c.ora_roma)}</p>
              </div>
            </div>
            <h3 className="mt-4 text-lg sm:text-xl font-semibold" style={{ color: INK, letterSpacing: '-0.01em' }}>{c.titolo}</h3>
          </div>

          {!post && !c.copertina_url && (
            <p className="inline-flex items-center gap-1.5 text-sm" style={{ color: MUTED }}>
              <ImageIcon className="w-4 h-4" /> La copertina arriva a breve.
            </p>
          )}

          <Caption clientId={clientId} contentId={c.id} testo={c.caption} apri={apriCaption} />

          <div className="mt-auto pt-1 flex flex-col gap-3" data-azioni>
            {richiesta && modo !== 'modifica' && <RichiestaInviata r={richiesta} appena={appena} />}
            {errore && <p className="text-sm" style={{ color: '#B42318' }}>{errore}</p>}
            {modo === 'scelta' && (
              <div className="flex gap-2">
                <button
                  onClick={() => setModo('approva')}
                  className="flex-auto sm:flex-none whitespace-nowrap inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 rounded-xl text-[14px] sm:text-[15px] font-semibold text-white transition hover:opacity-90"
                  style={{ backgroundColor: INK }}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Approva
                </button>
                <button
                  onClick={() => {
                    setErrore(null);
                    setModo('modifica');
                  }}
                  className="flex-auto sm:flex-none whitespace-nowrap inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 rounded-xl text-[14px] sm:text-[15px] font-semibold transition bg-white hover:bg-[#F4F5F7]"
                  style={{ color: INK, border: `1px solid ${INK}` }}
                >
                  <Pencil className="w-4 h-4" />
                  Chiedi una modifica
                </button>
              </div>
            )}
            {modo === 'approva' && (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={approva}
                  disabled={invio}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-[15px] font-semibold text-white transition disabled:opacity-60"
                  style={{ backgroundColor: '#1F7A4D' }}
                >
                  {invio ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  {post ? 'Sì, approvo foto e testo' : 'Sì, approvo video, copertina e testo'}
                </button>
                <button
                  onClick={() => setModo('scelta')}
                  disabled={invio}
                  className="inline-flex items-center justify-center px-5 py-3 rounded-xl text-[15px] font-medium transition"
                  style={{ color: INK, border: `1px solid ${LINE}` }}
                >
                  Annulla
                </button>
              </div>
            )}
            {modo === 'modifica' && (
              <FormModifica
                post={post}
                onInvia={chiedi}
                onAnnulla={() => setModo('scelta')}
                onCaption={() => {
                  setModo('scelta');
                  setApriCaption((n) => n + 1);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

/** Scheda piccola per approvati e post in programma; «Leggi il testo» apre la caption da leggere e cambiare. */
const SchedaPiccola: React.FC<{ c: ContenutoApprovazione; onApriCopertina: (u: string) => void; onApriVideo: (c: ContenutoApprovazione) => void; onApriTesto: (c: ContenutoApprovazione) => void }> = ({
  c,
  onApriCopertina,
  onApriVideo,
  onApriTesto,
}) => (
  <article className="rounded-2xl p-3 bg-white flex flex-col gap-3" style={{ border: `1px solid ${LINE}` }}>
    {c.tipo === 'post' ? (
      <button onClick={() => onApriTesto(c)} className="relative w-full aspect-[4/5] rounded-xl overflow-hidden bg-[#F4F5F7]" title="Apri il post">
        {c.slide_url?.[0] ? (
          c.slide_video?.[0] ? (
            <video src={`${c.slide_url[0]}#t=0.1`} preload="metadata" muted playsInline className="w-full h-full object-cover" />
          ) : (
            <img src={c.slide_url[0]} alt="" className="w-full h-full object-cover" loading="lazy" />
          )
        ) : (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-xs" style={{ color: MUTED }}>
            <ImageIcon className="w-5 h-5" /> Foto in preparazione
          </span>
        )}
        {(c.slide_url?.length ?? 0) > 1 && (
          <span className="absolute top-2 right-2 text-[11px] font-semibold text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>1/{c.slide_url!.length}</span>
        )}
      </button>
    ) : c.video_url ? (
      <button onClick={() => onApriVideo(c)} className="relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-black" title="Guarda il video">
        {c.copertina_url ? <img src={c.copertina_url} alt="" className="w-full h-full object-cover opacity-90" loading="lazy" /> : <video src={`${c.video_url}#t=0.1`} preload="metadata" muted playsInline className="w-full h-full object-cover" />}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
            <Film className="w-5 h-5" style={{ color: INK }} />
          </span>
        </span>
      </button>
    ) : (
      <Copertina url={c.copertina_url} onApri={onApriCopertina} grande />
    )}
    <div className="px-1 pb-1">
      <p className="text-[12px]" style={{ color: MUTED }}>Programmato per il</p>
      <p className="text-[13px] font-medium" style={{ color: INK }}>{dataLeggibile(c.data_roma, c.ora_roma)}</p>
      <p className="text-sm font-semibold mt-0.5 line-clamp-2" style={{ color: INK }}>{c.titolo}</p>
      <button onClick={() => onApriTesto(c)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg" style={{ color: INK, border: `1px solid ${LINE}` }}>
        <Pencil className="w-3.5 h-3.5" /> {c.caption ? 'Leggi e modifica il testo' : 'Scrivi il testo'}
      </button>
      {c.approvato && (
        <p className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium" style={{ color: '#1F7A4D' }}>
          <CheckCircle2 className="w-3.5 h-3.5" /> Approvato
        </p>
      )}
    </div>
  </article>
);

const Titolo: React.FC<{ testo: string; n: number; sotto?: string }> = ({ testo, n, sotto }) => (
  <div className="mb-4">
    <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2" style={{ color: INK }}>
      {testo}
      <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F0F1F4', color: INK }}>{n}</span>
    </h2>
    {sotto && <p className="text-sm mt-1" style={{ color: MUTED }}>{sotto}</p>}
  </div>
);

/* ── Caricamento da admin (02/11/2026) ─────────────────────────────────────
   Il titolare: «se vai su quella pagina con il token admin devi poter caricare il
   contenuto; se viene caricato deve subito mettere in lavorazione la copertina e il
   copy, così il cliente può approvare tutto». Il pannello c'è solo se pubblica,
   chiedendo a social chi è il token del browser, risponde admin. Il cliente non lo vede. */

type StatoLavoro = 'pronta' | 'in_lavorazione' | 'da_fare' | 'non_possibile' | 'errore';
interface ReelAdmin {
  id: string;
  titolo: string;
  stato: string;
  data_roma: string | null;
  ora_roma: string | null;
  video: 'manca' | 'in_preparazione' | 'pronto' | 'errore';
  video_errore: string | null;
  caricato_a_mano: boolean;
  copertina: StatoLavoro;
  caption: StatoLavoro;
}

const tokenAdmin = () => getCookie('bymarke_token') ?? '';

const Chip: React.FC<{ testo: string; tono: 'ok' | 'lavoro' | 'manca' | 'errore' }> = ({ testo, tono }) => {
  const stile = {
    ok: { backgroundColor: '#E8F5EE', color: '#1F7A4D' },
    lavoro: { backgroundColor: '#FFF7E8', color: '#8A5A00' },
    manca: { backgroundColor: '#F0F1F4', color: '#5B6270' },
    errore: { backgroundColor: '#FDECEC', color: '#B42318' },
  }[tono];
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={stile}>
      {tono === 'lavoro' && <Loader2 className="w-3 h-3 animate-spin" />}
      {tono === 'ok' && <Check className="w-3 h-3" />}
      {testo}
    </span>
  );
};

const chipVideo = (v: ReelAdmin['video']) =>
  v === 'pronto' ? <Chip testo="Video pronto" tono="ok" /> : v === 'in_preparazione' ? <Chip testo="Video in preparazione" tono="lavoro" /> : v === 'errore' ? <Chip testo="Video con errore" tono="errore" /> : <Chip testo="Video da caricare" tono="manca" />;

const chipLavoro = (nome: string, s: StatoLavoro) =>
  s === 'pronta' ? <Chip testo={`${nome} pronta`} tono="ok" /> : s === 'in_lavorazione' ? <Chip testo={`${nome} in lavorazione`} tono="lavoro" /> : s === 'errore' ? <Chip testo={`${nome}: errore, si riprova`} tono="errore" /> : s === 'non_possibile' ? <Chip testo={`${nome} non possibile`} tono="errore" /> : <Chip testo={`${nome} da fare`} tono="manca" />;

/** Manda il file a pubblica col progresso; risolve con il messaggio di errore o null. */
const caricaFile = (clientId: string, contentId: string, f: File, onProgresso: (p: number) => void) =>
  new Promise<string | null>((risolvi) => {
    const x = new XMLHttpRequest();
    x.open('POST', `/api/approvazioni/${clientId}/carica/${contentId}`);
    x.setRequestHeader('Authorization', `Bearer ${tokenAdmin()}`);
    const zip = /\.zip$/i.test(f.name);
    x.setRequestHeader('Content-Type', zip ? 'application/zip' : f.type === 'video/quicktime' || /\.mov$/i.test(f.name) ? 'video/quicktime' : 'video/mp4');
    x.setRequestHeader('X-Nome-File', encodeURIComponent(f.name));
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgresso(Math.round((e.loaded / e.total) * 100)); };
    x.onload = () => {
      if (x.status >= 200 && x.status < 300) return risolvi(null);
      if (x.status === 413) return risolvi('Il file supera 200 MB: va esportato più leggero');
      let m: string | null = null;
      try { m = (JSON.parse(x.responseText) as { messaggio?: string }).messaggio ?? null; } catch { /* risposta non JSON */ }
      risolvi(m || `Caricamento non riuscito (${x.status})`);
    };
    x.onerror = () => risolvi('Connessione interrotta: riprova');
    x.send(f);
  });

const RigaReelAdmin: React.FC<{ clientId: string; r: ReelAdmin; onCaricato: () => void }> = ({ clientId, r, onCaricato }) => {
  const input = useRef<HTMLInputElement>(null);
  const [progresso, setProgresso] = useState<number | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const scegli = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) { setErrore('Il file supera 200 MB: va esportato più leggero'); return; }
    setErrore(null);
    setProgresso(0);
    const e = await caricaFile(clientId, r.id, f, setProgresso);
    setProgresso(null);
    if (e) setErrore(e);
    else onCaricato();
  };
  const occupato = progresso !== null || r.video === 'in_preparazione';
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" style={{ borderTop: `1px solid ${LINE}` }}>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold truncate" style={{ color: INK }}>{r.titolo}</p>
        <p className="text-[12px]" style={{ color: MUTED }}>{r.data_roma ? dataLeggibile(r.data_roma, r.ora_roma) : 'Senza data'}</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {chipVideo(r.video)}
          {chipLavoro('Copertina', r.copertina)}
          {chipLavoro('Caption', r.caption)}
        </div>
        {r.video === 'errore' && r.video_errore && <p className="mt-1 text-[12px]" style={{ color: '#B42318' }}>{r.video_errore}</p>}
        {errore && <p className="mt-1 text-[12px]" style={{ color: '#B42318' }}>{errore}</p>}
        {progresso !== null && (
          <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F0F1F4' }}>
            <div className="h-full transition-all" style={{ width: `${progresso}%`, backgroundColor: INK }} />
          </div>
        )}
      </div>
      <input ref={input} type="file" accept="video/mp4,video/quicktime,.mp4,.mov,.zip" className="hidden" onChange={(e) => { void scegli(e.target.files?.[0]); e.target.value = ''; }} />
      <button
        onClick={() => input.current?.click()}
        disabled={occupato}
        className="flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold transition disabled:opacity-50"
        style={r.video === 'manca' ? { backgroundColor: INK, color: '#fff' } : { backgroundColor: '#fff', color: INK, border: `1px solid ${LINE}` }}
      >
        {progresso !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> {progresso}%</> : <><Upload className="w-4 h-4" /> {r.video === 'manca' ? 'Carica il video' : 'Sostituisci il video'}</>}
      </button>
    </li>
  );
};

const PannelloAdmin: React.FC<{ clientId: string; onRicarica?: () => void }> = ({ clientId, onRicarica }) => {
  const [reel, setReel] = useState<ReelAdmin[] | null>(null);
  const [aperto, setAperto] = useState(true);
  const [soloSenza, setSoloSenza] = useState(false);
  const [osservaFino, setOsservaFino] = useState(0);

  const leggi = useCallback(async () => {
    try {
      const r = await fetch(`/api/approvazioni/${clientId}/admin`, { cache: 'no-store', headers: { Authorization: `Bearer ${tokenAdmin()}` } });
      const j = (await r.json()) as { admin?: boolean; reel?: ReelAdmin[] };
      if (j.admin) setReel(j.reel ?? []);
    } catch { /* al prossimo giro */ }
  }, [clientId]);

  useEffect(() => { void leggi(); }, [leggi]);

  // Mentre qualcosa è in lavorazione (o subito dopo un caricamento) si rilegge ogni 8 secondi,
  // e con lui la sezione «Da approvare», così il reel compare appena è pronto.
  const inCorso = (reel ?? []).some((r) => r.video === 'in_preparazione' || r.copertina === 'in_lavorazione' || r.caption === 'in_lavorazione');
  useEffect(() => {
    if (!inCorso && Date.now() > osservaFino) return;
    const t = setInterval(() => {
      void leggi();
      onRicarica?.();
    }, 8000);
    return () => clearInterval(t);
  }, [inCorso, osservaFino, leggi, onRicarica]);

  const caricato = () => {
    setOsservaFino(Date.now() + 15 * 60_000);
    void leggi();
    onRicarica?.();
  };

  const visibili = (reel ?? []).filter((r) => !soloSenza || r.video === 'manca' || r.video === 'errore');
  return (
    <section className="rounded-2xl p-4 sm:p-5 bg-white" style={{ border: `1.5px dashed ${INK}` }}>
      <button onClick={() => setAperto((a) => !a)} className="w-full flex items-start justify-between gap-3 text-left">
        <div>
          <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold" style={{ color: MUTED }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Solo admin, il cliente non lo vede
          </p>
          <h2 className="mt-1 text-base sm:text-lg font-semibold" style={{ color: INK }}>Carica un reel da far approvare</h2>
          <p className="text-sm mt-0.5" style={{ color: MUTED }}>Appena il video è caricato partono la copertina e il copy; quando il video è pronto il reel compare qui sotto in «Da approvare».</p>
        </div>
        <ChevronDown className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform ${aperto ? 'rotate-180' : ''}`} style={{ color: INK }} />
      </button>
      {aperto && (
        <div className="mt-3">
          {reel === null ? (
            <div className="flex items-center gap-2 py-4 text-sm" style={{ color: MUTED }}><Loader2 className="w-4 h-4 animate-spin" /> Carico i reel del cliente</div>
          ) : reel.length === 0 ? (
            <p className="py-3 text-sm" style={{ color: MUTED }}>Nessun reel in programma per questo cliente: crealo prima in social (Copywriter).</p>
          ) : (
            <>
              <label className="inline-flex items-center gap-2 text-[13px] mb-1 cursor-pointer" style={{ color: INK }}>
                <input type="checkbox" checked={soloSenza} onChange={(e) => setSoloSenza(e.target.checked)} />
                Solo quelli senza video
              </label>
              <ul className="max-h-[460px] overflow-y-auto">
                {visibili.map((r) => <RigaReelAdmin key={r.id} clientId={clientId} r={r} onCaricato={caricato} />)}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
};

const ClientApprovazioni: React.FC<{
  clientId: string;
  contenuti: ContenutoApprovazione[] | null;
  errore: string | null;
  onApprovato: (id: string) => void;
  /** Admin di social sul link: compare il pannello per caricare i reel (02/11/2026). */
  admin?: boolean;
  onRicarica?: () => void;
}> = ({ clientId, contenuti, errore, onApprovato, admin = false, onRicarica }) => {
  const [copertinaAperta, setCopertinaAperta] = useState<string | null>(null);
  const [videoAperto, setVideoAperto] = useState<ContenutoApprovazione | null>(null);
  const [testoAperto, setTestoAperto] = useState<ContenutoApprovazione | null>(null);
  const [testiSalvati, setTestiSalvati] = useState<Record<string, string>>({});
  // Reel e post grafici divisi (02/11/2026, il titolare: «una divisione da approvare i post grafici e approvare i reel»).
  const [scelta, setScelta] = useState<'reel' | 'post' | null>(null);

  const approva = useCallback(
    async (id: string) => {
      const r = await fetch(`/api/approvazioni/${clientId}/approva/${id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.messaggio || 'Non è andata, riprova fra poco');
      onApprovato(id);
    },
    [clientId, onApprovato],
  );

  const chiediModifica = useCallback(
    async (id: string, cosa: string[], testo: string): Promise<RichiestaModifica> => {
      const r = await fetch(`/api/approvazioni/${clientId}/modifica/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cosa, testo }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.richiesta) throw new Error(j?.messaggio || 'Non siamo riusciti a inviare la richiesta, riprova fra poco');
      return j.richiesta as RichiestaModifica;
    },
    [clientId],
  );

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCopertinaAperta(null);
        setVideoAperto(null);
        setTestoAperto(null);
      }
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  if (errore) {
    return (
      <div className="flex flex-col gap-6">
        {admin && <PannelloAdmin clientId={clientId} onRicarica={onRicarica} />}
        <div className="rounded-2xl p-6 bg-white text-sm" style={{ border: `1px solid ${LINE}`, color: MUTED }}>{errore}</div>
      </div>
    );
  }
  if (!contenuti) {
    return (
      <div className="flex items-center justify-center py-16" style={{ color: MUTED }}>
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const reelDaApprovare = contenuti.filter((c) => c.si_puo_approvare && c.tipo !== 'post').length;
  const postDaApprovare = contenuti.filter((c) => c.si_puo_approvare && c.tipo === 'post').length;
  // Di base la scheda con qualcosa da approvare, prima i reel.
  const tipo: 'reel' | 'post' = scelta ?? (reelDaApprovare === 0 && postDaApprovare > 0 ? 'post' : 'reel');
  const delTipo = contenuti.filter((c) => (tipo === 'post' ? c.tipo === 'post' : c.tipo !== 'post'));
  const daApprovare = delTipo.filter((c) => c.si_puo_approvare);
  const approvati = delTipo.filter((c) => c.approvato);
  const post = tipo === 'post' ? delTipo.filter((c) => !c.approvato && !c.si_puo_approvare) : [];
  const nomeTipo = tipo === 'post' ? 'post' : 'reel';

  const schede: { key: 'reel' | 'post'; nome: string; n: number }[] = [
    { key: 'reel', nome: 'Reel', n: reelDaApprovare },
    { key: 'post', nome: 'Post grafici', n: postDaApprovare },
  ];

  return (
    <div className="flex flex-col gap-10">
      {admin && <PannelloAdmin clientId={clientId} onRicarica={onRicarica} />}
      <div className="-mb-4 inline-flex self-start p-1 rounded-xl bg-white" style={{ border: `1px solid ${LINE}` }}>
        {schede.map((x) => {
          const on = tipo === x.key;
          return (
            <button
              key={x.key}
              onClick={() => setScelta(x.key)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition"
              style={on ? { backgroundColor: INK, color: '#fff' } : { color: MUTED }}
            >
              {x.nome}
              {x.n > 0 && (
                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full" style={on ? { backgroundColor: '#fff', color: INK } : { backgroundColor: INK, color: '#fff' }}>
                  {x.n}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <section>
        <Titolo
          testo={tipo === 'post' ? 'Post grafici da approvare' : 'Reel da approvare'}
          n={daApprovare.length}
          sotto={
            tipo === 'post'
              ? 'Scorri le foto e leggi il testo. Se va tutto bene premi Approva, se vuoi cambiare qualcosa premi Chiedi una modifica.'
              : 'Guarda il video, la copertina e il testo. Se va tutto bene premi Approva, se vuoi cambiare qualcosa premi Chiedi una modifica.'
          }
        />
        {daApprovare.length === 0 ? (
          <div className="rounded-2xl p-6 bg-white flex items-center gap-3" style={{ border: `1px solid ${LINE}` }}>
            <CheckCircle2 className="w-5 h-5" style={{ color: '#1F7A4D' }} />
            <p className="text-sm" style={{ color: INK }}>{tipo === 'post' ? 'Non ci sono post da approvare in questo momento.' : 'Non ci sono reel da approvare in questo momento.'}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {daApprovare.map((c) => (
              <SchedaDaApprovare key={c.id} clientId={clientId} c={c} onApprova={approva} onChiediModifica={chiediModifica} onApriCopertina={setCopertinaAperta} />
            ))}
          </div>
        )}
      </section>

      {/* Gli approvati stanno nel Calendario, segnati «Approvato» (29/10/2026, il titolare). */}
      {approvati.length > 0 && (
        <p className="-mt-6 inline-flex items-center gap-1.5 text-sm" style={{ color: MUTED }}>
          <CheckCircle2 className="w-4 h-4" style={{ color: '#1F7A4D' }} />
          {approvati.length === 1 ? `1 ${nomeTipo} approvato: lo trovi nel Calendario.` : `${approvati.length} ${nomeTipo} approvati: li trovi nel Calendario.`}
        </p>
      )}

      {post.length > 0 && (
        <section>
          <Titolo testo="Post in programma" n={post.length} />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {post.map((c) => (
              <SchedaPiccola key={c.id} c={c} onApriCopertina={setCopertinaAperta} onApriVideo={setVideoAperto} onApriTesto={setTestoAperto} />
            ))}
          </div>
        </section>
      )}

      {/* Niente «In preparazione» nel link del cliente (29/10/2026, il titolare): vede solo quello da approvare. */}

      {testoAperto && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: 'rgba(10,12,16,0.82)' }} onClick={() => setTestoAperto(null)}>
          <div className="w-full sm:max-w-[560px] max-h-[92vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[12px]" style={{ color: MUTED }}>{dataLeggibile(testoAperto.data_roma, testoAperto.ora_roma)}</p>
                <h3 className="text-lg font-semibold" style={{ color: INK }}>{testoAperto.titolo}</h3>
              </div>
              <button onClick={() => setTestoAperto(null)} className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F4F5F7' }} aria-label="Chiudi">
                <X className="w-5 h-5" style={{ color: INK }} />
              </button>
            </div>
            {testoAperto.tipo === 'post' && (testoAperto.slide_url?.length ?? 0) > 0 && (
              <div className="w-full max-w-[360px] mx-auto">
                <Foto urls={testoAperto.slide_url ?? []} video={testoAperto.slide_video} />
              </div>
            )}
            <Caption
              clientId={clientId}
              contentId={testoAperto.id}
              testo={testiSalvati[testoAperto.id] ?? testoAperto.caption}
              onSalvato={(t) => setTestiSalvati((x) => ({ ...x, [testoAperto.id]: t }))}
            />
          </div>
        </div>
      )}

      {(copertinaAperta || videoAperto) && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(10,12,16,0.82)' }}
          onClick={() => {
            setCopertinaAperta(null);
            setVideoAperto(null);
          }}
        >
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center" aria-label="Chiudi">
            <X className="w-5 h-5" style={{ color: INK }} />
          </button>
          <div className="w-full max-w-[400px]" onClick={(e) => e.stopPropagation()}>
            {videoAperto?.video_url ? (
              <video src={videoAperto.video_url} poster={videoAperto.copertina_url ?? undefined} controls autoPlay playsInline className="w-full max-h-[85vh] aspect-[9/16] rounded-2xl bg-black object-contain" />
            ) : copertinaAperta ? (
              <img src={copertinaAperta} alt="Copertina" className="w-full max-h-[85vh] object-contain rounded-2xl" />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientApprovazioni;
